import { Building2Icon, CheckIcon, NegativeThumb, PositiveThumb } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import { getReviewLabel } from 'components/card/ReviewCard';
import CellButton from 'components/table/components/CellButton';
import EmptyCell from 'components/table/components/EmptyCell';
import TableCell from 'components/table/components/TableCell';
import TableRow from 'components/table/components/TableRow';
import Tooltips from 'components/tooltips/Tooltips';
import { ROW_LEAVE_CLASS } from 'hooks/useLeavingItems';
import useText from 'hooks/useText';

const fullName = (person) => [person?.firstname, person?.lastname].filter(Boolean).join(' ');

/**
 * A review in the table, laid out as a task's row: the temp worker (avatar,
 * name, e-mail), the mission and its company, the review itself, then who
 * left it (an avatar, on the right). A review to treat ends with the check
 * that treats it; the whole row opens it too.
 */
const ReviewTableRow = ({ review, index, untreated, selectedId, leaving, onLeaveEnd, onOpen, onOpenWorker, onOpenCompany }) => {
  const { text } = useText('reviews');
  const positive = review.type === 'positive';
  const worker = review.user;
  const author = review.employee;
  return (
    <TableRow
      itemId={review._id}
      selectedId={selectedId}
      clickable
      onClick={() => onOpen(review._id)}
      className={`h-[72px] ${leaving ? ROW_LEAVE_CLASS : 'motion-safe:animate-fadeUp'}`}
      style={leaving ? undefined : { animationDelay: `${Math.min(index, 9) * 30}ms` }}
      onAnimationEnd={onLeaveEnd}
    >
      <TableCell compact position="first">
        <span className="flex min-w-0 max-w-[260px] items-center gap-3">
          <EmployeeAvatar employee={worker} size={32} />
          <span className="min-w-0">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onOpenWorker(worker?._id);
              }}
              className="block max-w-full truncate font-medium text-gray-900 hover:underline"
            >
              {fullName(worker)}
            </button>
            <span className="block truncate text-xs text-gray-500">{worker?.email}</span>
          </span>
        </span>
      </TableCell>
      <TableCell compact>
        <span className="flex min-w-0 max-w-[260px] flex-col">
          <span className="truncate text-gray-900">
            {review.mission?.title} <span className="text-gray-500">{text('missionNumber', { number: review.mission?.id_mission })}</span>
          </span>
          {review.id_company ? (
            <CellButton icon={Building2Icon} tooltip={text('openCompany')} onClick={() => onOpenCompany(review.id_company)}>
              {review.name_commercial}
            </CellButton>
          ) : (
            <EmptyCell />
          )}
        </span>
      </TableCell>
      <TableCell compact>
        <span className="flex min-w-0 max-w-[320px] items-center gap-3">
          <span
            className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
              positive ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'
            }`}
          >
            {positive ? <PositiveThumb wh={16} color="currentColor" /> : <NegativeThumb wh={16} color="currentColor" />}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-gray-900">{getReviewLabel(review)}</span>
            {review.comment && <span className="block truncate text-xs text-gray-500">{review.comment}</span>}
          </span>
        </span>
      </TableCell>
      <TableCell compact>
        {author ? (
          <span className="flex justify-end">
            <Tooltips text={fullName(author)} placement="top">
              <span className="flex">
                <EmployeeAvatar employee={author} size={28} />
              </span>
            </Tooltips>
          </span>
        ) : (
          <EmptyCell />
        )}
      </TableCell>
      <TableCell compact position="last">
        {untreated && (
          <Tooltips text={text('treat')} placement="top">
            <button
              type="button"
              aria-label={text('treat')}
              onClick={(event) => {
                event.stopPropagation();
                onOpen(review._id);
              }}
              className="flex size-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50"
            >
              <CheckIcon w={14} h={10} color="currentColor" />
            </button>
          </Tooltips>
        )}
      </TableCell>
    </TableRow>
  );
};

export default ReviewTableRow;
